import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {validateEmail, validateFieldLength, validateNINumber} from '../../../utility/validationUtil';
import moment from 'moment';
import {EditableFields} from '../../../shared-components/EditableFields';
import useTextFields from '../../../utility/hooks/useTextFields';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {STAFF_ROLES} from './staffRoles';
import {photoLineSx} from '../Profiles/Components/ProfileStatus';
import {FIT_COLUMNS_SX, useFitColumns} from '../../../shared-components/fitColumns';
import {Alert, Avatar, Backdrop, Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import StaffContactTable from './StaffContactTable';
import StaffAddContactBox from './StaffAddContactBox';
import ProfileInfoSection from '../../../shared-components/ProfileInfoSection';
import {useConfirm} from 'material-ui-confirm';
import AdmicityAddressLookup from '../../../shared-components/AdmicityAddressLookup';
import {
    useGetContactTitlesQuery,
    useGetEmployeeContactTypesQuery,
    useGetNextOfKinTypesQuery
} from '../../../api/services/lookupsService';
import {useUpdateEmployeeProfileMutation} from '../../../api/services/employeeService';
import useNotification from '../../../utility/hooks/useNotification';
import useUser from '../../../utility/hooks/useUser';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../shared-components/AdmicityForm';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
    getStaffSectionAccess,
    hasAnyEditableStaffSection,
    STAFF_PERMISSION_MODULES,
    STAFF_SECTION_ACCESS
} from './staffProfileSectionAccess';

const EMPTY = [];
const LOCAL_ROW_ID_FIELD = '_localRowId';

const LockedSectionHint = () => (
    <Typography variant="caption" color="text.secondary">
        This section is locked. Changes require evidence and must be made by the school.
    </Typography>
);

const addLocalRowIds = (items = [], itemType, nextRowIdRef) => items.map(item => {
    if (item?.[LOCAL_ROW_ID_FIELD]) {
        return item;
    }

    return {
        ...item,
        [LOCAL_ROW_ID_FIELD]: item?.id != null
            ? `${itemType}-existing-${item.id}`
            : `${itemType}-new-${nextRowIdRef.current++}`
    };
});

const removeLocalRowIds = (items = []) => items.map(({
                                                         [LOCAL_ROW_ID_FIELD]: _removedLocalRowId,
                                                         ...item
                                                     }) => item);

const trimStringValues = (item) => Object.fromEntries(
    Object.entries(item).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
);

const hasFieldValue = (value) => value !== undefined && value !== null && value !== '';

// Maria, 18 October 2026: an address as plain lines until someone clicks on 'Edit', as on the student profile
// Maria, 19 October 2026: the next of kin's address in the Contacts table, one line at a time.
const AddressLines = ({address, plain, tight}) => {
    const street = [address?.houseNumber, address?.street].filter(Boolean).join(' ');
    const lines = address
        ? [address.apartment, address.houseName, street, address.district, address.town, address.county, address.postcode]
            .filter(Boolean)
        : [];
    return lines.length
        ? <Stack rowGap={tight ? 0 : 0.25} sx={tight ? {lineHeight: 1.25} : plain ? {} : {fontSize: 16, lineHeight: 1.35}} data-testid="staff-address-lines">
            {lines.map(line => <Box component="span" key={line}>{line}</Box>)}
        </Stack>
        : <Typography color="text.secondary">-</Typography>;
};

AddressLines.propTypes = {
    address: PropTypes.object,
    plain: PropTypes.bool,
    // in a table row: no space between the lines
    tight: PropTypes.bool
};

// Maria, 18 October 2026: 'Home Address' in the column of questions, in the same font as 'Date Of Birth' and 'Email',
// with the address in the column of answers
const AddressRow = ({address, label = 'Home Address', fitColumns = false}) => (
    <Grid container>
        <Grid item container xs={12} paddingTop={0.75} paddingBottom={0.75} borderBottom={theme => `1px solid ${theme.palette.divider}`}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {xs: 'min(150px, 50%) minmax(0, 1fr)', md: 'min(180px, 50%) minmax(0, 1fr)'},
                columnGap: 1,
                width: '100%',
                alignItems: 'start',
                ...(fitColumns ? FIT_COLUMNS_SX : {})
            }}
            >
                <Typography variant="body2" fontWeight="bold" data-fit-label>{`${label}:`}</Typography>
                <Typography variant="body2" component="div" data-fit-answer sx={{minWidth: 0, overflowWrap: 'anywhere'}}><AddressLines address={address} plain/></Typography>
            </Box>
        </Grid>
    </Grid>
);

AddressRow.propTypes = {
    address: PropTypes.object,
    label: PropTypes.string,
    fitColumns: PropTypes.bool
};

// Maria, 18 October 2026: the staff contacts table in the same style as the student contacts table: a light green
// heading row, tighter rows, and the buttons column with a solid background
// Maria, 18 October 2026: the two boxes on the Profile tab are the same height.
const EQUAL_HEIGHT_SX = {display: 'flex', flexDirection: 'column', '& > .MuiCard-root': {flex: 1}, '& .MuiCardContent-root:last-child': {pb: 1.25}};

const SECTION_HEADING_SX = {fontSize: 15, mb: 0.75};

// Maria, 18 October 2026: the staff profile in tabs, like the student profile. 'sections' lists the sections the open
// tab shows; without it, every section shows (My Profile).
export const STAFF_TAB_SECTIONS = {
    profile: ['basicInformation', 'homeAddress'],
    contacts: ['contacts', 'nextOfKin', 'nextOfKinAddress'],
    banking: ['identificationAndBanking'],
    cars: ['cars']
};

// Maria, 18 October 2026: as little space as possible in the address box while editing, as on the student profile
const COMPACT_ADDRESS_SX = {
    mt: 0.5,
    '& .MuiGrid-container': {marginTop: '0 !important', marginLeft: '-8px', width: 'calc(100% + 8px)'},
    '& .MuiGrid-item': {paddingTop: '8px !important', paddingLeft: '8px !important'}
};

const EmployeeProfile = ({data, isLoading, staffPermissions, sections, onCancel, framed}) => {
    const inTab = key => !sections || sections.includes(key);
    const confirm = useConfirm();
    const {user} = useUser();
    const {data: titles = EMPTY, isLoading: isTitlesLoading} = useGetContactTitlesQuery();
    const {data: nextOfKinTypes = EMPTY, isLoading: isNextOfKinTypesLoading} = useGetNextOfKinTypesQuery();
    const {data: employeeContactTypes = EMPTY, isLoading: isContactTypesLoading} = useGetEmployeeContactTypesQuery();
    const [isEditMode, setIsEditMode] = useState(false);
    const [openNextOfKinDialog, setOpenNextOfKinDialog] = useState(false);
    // Maria, 19 October 2026: the Next of Kin box on two screens, the person and then the address
    const [nextOfKinStep, setNextOfKinStep] = useState('details');
    useEffect(() => {
        if (openNextOfKinDialog) setNextOfKinStep('details');
    }, [openNextOfKinDialog]);
    // Maria, 19 October 2026: on the Contacts tab each row has its own Edit and Delete, and a change saves at once.
    const rowsEditable = Boolean(sections?.includes('contacts') || sections?.includes('cars')) && data?.simsId != null;
    const [saveRequest, setSaveRequest] = useState(0);
    // Maria, 19 October 2026: each table ends in line with the right-hand edge of a tab.
    const contactsRef = useRef(null);
    const [toCarsTab, setToCarsTab] = useState(null);
    const [indent, setIndent] = useState(0);
    const [firstBoxWidth, setFirstBoxWidth] = useState(null);

    useEffect(() => {
        // Maria, 19 October 2026: the Cars table ends in line with the Banking tab.
        // the contact tables, with their wider Name and Address columns, end in line with the Forms tab
        // Maria, 19 October 2026: the Bank and NI box starts at the left edge of its tab and ends at the Cars tab
        const bankingTab = sections?.includes('identificationAndBanking');
        // Maria, 19 October 2026: the Cars table starts at the left edge of the Cars tab and ends at the Forms tab
        const carsTab = sections?.includes('cars');
        const contactsTab = sections?.includes('contacts');
        const endTab = carsTab || contactsTab ? 'forms' : bankingTab || sideBySideMd === 6 ? 'cars' : canViewCars ? 'banking' : null;

        if (!endTab || framed) {
            setToCarsTab(null);
            setIndent(0);
            // Maria, 19 October 2026: on the framed Profile tab, the first box ends in line with the Contacts tab
            const grid = contactsRef.current;
            const contactsTab = document.querySelector('[data-tab-path="contacts"]');
            const firstItem = grid?.firstElementChild;
            const firstBox = firstItem?.querySelector('.MuiCard-root');
            // the item's width now, corrected by how far its box falls short of (or past) the tab's edge
            const split = framed && sideBySideMd === 6 && firstBox && contactsTab
                ? Math.round(firstItem.getBoundingClientRect().width + contactsTab.getBoundingClientRect().right - firstBox.getBoundingClientRect().right)
                : null;
            setFirstBoxWidth(split && split > 200 && split < grid.getBoundingClientRect().width - 200 ? split : null);
            return undefined;
        }
        const measure = () => {
            const tab = document.querySelector(`[data-tab-path="${endTab}"]`);
            const box = contactsRef.current;
            // Maria, 19 October 2026: the contact tables start in line with the left edge of the Contacts tab
            const startTab = contactsTab ? 'contacts' : bankingTab ? 'banking' : carsTab ? 'cars' : null;
            const start = startTab ? document.querySelector(`[data-tab-path="${startTab}"]`) : null;
            // where the tables would start with no indent
            const parentLeft = box ? box.getBoundingClientRect().left - (parseFloat(getComputedStyle(box).marginLeft) || 0) : 0;
            const indent = start ? Math.max(0, Math.round(start.getBoundingClientRect().left - parentLeft)) : 0;
            const width = tab && box ? Math.round(tab.getBoundingClientRect().right - parentLeft - indent) : 0;
            // the tab row is on screen, so a box that ends at a tab always fits the page
            // Maria, 19 October 2026: and only while the tabs are on one row; on a phone the tables use the full width
            const sameRow = Boolean(start) && Boolean(tab) && Math.abs(start.getBoundingClientRect().top - tab.getBoundingClientRect().top) < 8;
            const fits = sameRow && width > 0 && tab.getBoundingClientRect().right <= document.documentElement.clientWidth;
            setToCarsTab(fits ? width : null);
            setIndent(fits ? indent : 0);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    });
    const [updateEmployeeProfile, {
        isSuccess: isUpdateEmployeeProfileSuccess,
        isError: isUpdateEmployeeProfileError,
        error
    }] = useUpdateEmployeeProfileMutation();
    const {showErrorNotification, showSuccessNotification, showDetailedErrorNotification} = useNotification();
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedNextOfKinAddress, setSelectedNextOfKinAddress] = useState(null);
    const carRowKeyRef = useRef(0);
    const contactRowKeyRef = useRef(0);
    const [cars, setCars] = useState(() => addLocalRowIds(data?.cars, 'car', carRowKeyRef));
    const [openCarDialog, setOpenCarDialog] = useState(false);
    const [editingCar, setEditingCar] = useState(null);
    const [contacts, setContacts] = useState(() => addLocalRowIds(data?.contacts, 'contact', contactRowKeyRef));
    const [openContactDialog, setOpenContactDialog] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [nextOfKinAddressKey, setNextOfKinAddressKey] = useState(0);
    const ignoreAddressSelect = useCallback(() => undefined, []);
    const isSomeQueryPending = isLoading || isTitlesLoading || isNextOfKinTypesLoading || isContactTypesLoading;
    const isReadOnlyProfile = data?.simsId == null;
    const permissions = staffPermissions || user.permissions || EMPTY;
    const basicInformationAccess = getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES.basicInformation);
    const identificationAndBankingAccess = getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES.identificationAndBanking);
    const homeAddressAccess = getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES.homeAddress);
    const carsAccess = getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES.cars);
    const contactsAccess = getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES.contacts);
    const nextOfKinAccess = getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES.nextOfKin);
    const nextOfKinAddressAccess = getStaffSectionAccess(permissions, STAFF_PERMISSION_MODULES.nextOfKinAddress);
    const canEditBasicInformation = basicInformationAccess === STAFF_SECTION_ACCESS.edit;
    const canEditIdentificationAndBanking = identificationAndBankingAccess === STAFF_SECTION_ACCESS.edit;
    const canEditHomeAddress = homeAddressAccess === STAFF_SECTION_ACCESS.edit;
    const canEditCars = carsAccess === STAFF_SECTION_ACCESS.edit;
    const canEditContacts = contactsAccess === STAFF_SECTION_ACCESS.edit;
    const canEditNextOfKin = nextOfKinAccess === STAFF_SECTION_ACCESS.edit;
    const canEditNextOfKinAddress = nextOfKinAddressAccess === STAFF_SECTION_ACCESS.edit;
    const canViewBasicInformation = basicInformationAccess !== STAFF_SECTION_ACCESS.hide && inTab('basicInformation');
    const canViewIdentificationAndBanking = identificationAndBankingAccess !== STAFF_SECTION_ACCESS.hide && inTab('identificationAndBanking');
    const canViewHomeAddress = homeAddressAccess !== STAFF_SECTION_ACCESS.hide && inTab('homeAddress');
    const canViewCars = carsAccess !== STAFF_SECTION_ACCESS.hide && inTab('cars');
    const canViewContacts = contactsAccess !== STAFF_SECTION_ACCESS.hide && inTab('contacts');
    const canViewNextOfKin = nextOfKinAccess !== STAFF_SECTION_ACCESS.hide && inTab('nextOfKin');
    const canViewNextOfKinAddress = nextOfKinAddressAccess !== STAFF_SECTION_ACCESS.hide && inTab('nextOfKinAddress');
    const canEditAnyStaffSection = hasAnyEditableStaffSection(permissions);
    const hasLeftColumnSections = canViewBasicInformation ||
        canViewIdentificationAndBanking ||
        canViewHomeAddress;
    const hasRightColumnSections = canViewCars ||
        canViewContacts ||
        canViewNextOfKin ||
        canViewNextOfKinAddress;
    const leftColumnMd = hasRightColumnSections ? 6 : 12;
    const rightColumnMd = hasLeftColumnSections ? 6 : 12;
    // Maria, 18 October 2026: on the Profile tab, Basic Information and Home Address side by side, like the top row of
    // the student profile
    const sideBySideMd = !hasRightColumnSections && canViewBasicInformation && canViewHomeAddress ? 6 : 12;
    const homeAddressDataKey = useMemo(() => JSON.stringify(data?.address || null), [data?.address]);
    const nextOfKinAddressDataKey = useMemo(() => JSON.stringify(data?.nextOfKinAddress || null), [data?.nextOfKinAddress]);

    useEffect(() => {
        if (isUpdateEmployeeProfileSuccess) {
            showSuccessNotification('Profile updated successfully');
            setIsEditMode(false);
        } else if (isUpdateEmployeeProfileError) {
            if (error?.data?.validationErrors) {
                showDetailedErrorNotification('Failed to submit form', error.data.validationErrors);
            } else {
                showErrorNotification('Failed to update profile');
            }
        }
    }, [isUpdateEmployeeProfileSuccess, isUpdateEmployeeProfileError]);

    useEffect(() => {
        if (!isEditMode) {
            setCars(addLocalRowIds(data?.cars, 'car', carRowKeyRef));
            setContacts(addLocalRowIds(data?.contacts, 'contact', contactRowKeyRef));
            setSelectedAddress(data?.address || null);
            setSelectedNextOfKinAddress(data?.nextOfKinAddress || null);
        }
    }, [data?.cars, data?.contacts, data?.address, data?.nextOfKinAddress, isEditMode]);

    const basicInfoTextFields = useMemo(() =>
        [
            {
                id: 'titleId',
                label: 'Title',
                name: 'titleId',
                initialValue: data?.titleId,
                required: false,
                type: 'select',
                select: true,
                nullableOnEmpty: true,
                options: titles?.map(title => ({
                    title: title.description,
                    value: title.id,
                }))
            },
            {
                id: 'profile_forename',
                label: 'Forename',
                name: 'legalForename',
                initialValue: data?.legalForename,
                required: true,
                validate: (value) => validateFieldLength(value, 60),
            },
            {
                id: 'profile_middleName',
                label: 'Middle Name',
                name: 'middleName',
                initialValue: data?.middleName,
                required: false,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_surname',
                label: 'Surname',
                name: 'legalSurname',
                initialValue: data?.legalSurname,
                required: true,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_preferredForename',
                label: 'Preferred Forename',
                name: 'preferredForename',
                initialValue: data?.preferredForename,
                required: true,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_preferredSurname',
                label: 'Preferred Surname',
                name: 'preferredSurname',
                initialValue: data?.preferredSurname,
                required: true,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_dateOfBirth',
                label: 'Date Of Birth',
                name: 'dateOfBirth',
                type: 'date',
                inputType: 'date',
                initialValue: data?.dateOfBirth ? moment.utc(data?.dateOfBirth) : null,
                required: true,
                minDate: moment(new Date('1900-01-01')),
                maxDate: moment().subtract(12, 'years'),
                nullableOnEmpty: true
            },
            {
                id: 'profile_email',
                label: 'Email',
                name: 'email',
                initialValue: data?.email,
                required: true,
                validate: validateEmail,
                nullableOnEmpty: true
            },
            {
                id: 'profile_phone',
                label: 'Mobile Number',
                name: 'mobileNumber',
                initialValue: data?.mobileNumber,
                validate: (value) => validateFieldLength(value, 20, false),
                required: false
            }
        ], [data, titles]);

    const nextOfKinTextFields = useMemo(() =>
        [
            {
                id: 'nextOfKinForename',
                label: 'Forename',
                name: 'nextOfKinForename',
                initialValue: data?.nextOfKinForename,
                validate: (value) => validateFieldLength(value, 60, false),
                required: false,
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinMiddleName',
                label: 'Middle Name',
                name: 'nextOfKinMiddleName',
                initialValue: data?.nextOfKinMiddleName,
                validate: (value) => validateFieldLength(value, 60, false),
                required: false,
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinSurname',
                label: 'Surname',
                name: 'nextOfKinSurname',
                initialValue: data?.nextOfKinSurname,
                validate: (value, fields) => {
                    const hasOtherFields = Object.entries(fields).some(([key, field]) => {
                        if (key === 'nextOfKinSurname') return false;
                        const value = field.textFieldProps.value;
                        return value !== undefined && value !== null && value !== '';
                    });

                    if (!hasOtherFields) {
                        return {
                            isValid: true,
                            message: ''
                        };
                    }

                    if (!value || value.trim() === '') {
                        return {
                            isValid: false,
                            message: 'Surname is required when other next of kin information is provided'
                        };
                    }

                    const lengthValidation = validateFieldLength(value, 60);
                    return {
                        isValid: lengthValidation.isValid,
                        message: lengthValidation.message
                    };
                },
                required: false
            },
            {
                id: 'nextOfKinTelephone',
                label: 'Telephone',
                name: 'nextOfKinTelephone',
                initialValue: data?.nextOfKinTelephone,
                validate: (value) => validateFieldLength(value, 20, false),
                required: false,
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinTitleCode',
                label: 'Title',
                name: 'nextOfKinTitleCode',
                initialValue: data?.nextOfKinTitleCode,
                required: false,
                type: 'select',
                select: true,
                nullableOnEmpty: true,
                options: titles?.map(title => ({
                    title: title.description,
                    value: title.code,
                })),
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinTypeCode',
                label: 'Relationship',
                name: 'nextOfKinTypeCode',
                initialValue: data?.nextOfKinTypeCode,
                required: false,
                type: 'select',
                select: true,
                nullableOnEmpty: true,
                options: nextOfKinTypes?.map(t => ({
                    title: t.description,
                    value: t.code,
                })),
                dependencies: ['nextOfKinSurname']
            }
        ], [data, titles, nextOfKinTypes]);

    const identificationBankingDetailsTextFields = useMemo(() =>
        [
            {
                id: 'profile_NINumber',
                label: 'NI Number',
                name: 'niNumber',
                initialValue: data?.niNumber,
                required: false,
                masked: true,
                validate: validateNINumber
            },
            {
                id: 'profile_bankName',
                label: 'Bank Name',
                name: 'bankName',
                initialValue: data?.bankName,
                required: false,
                validate: (value) => validateFieldLength(value, 50),
            },
            {
                id: 'profile_bankSortCode',
                label: 'Bank Sort Code',
                name: 'bankSortCode',
                initialValue: data?.bankSortCode,
                required: false,
                masked: true,
                validate: (value) => {
                    if (!value) {
                        return {
                            isValid: true,
                            message: ''
                        };
                    }
                    const cleanValue = value.replace(/\s/g, '');
                    if (!/^\d+$/.test(cleanValue)) {
                        return {
                            isValid: false,
                            message: 'Sort code must contain only digits'
                        };
                    }
                    if (cleanValue.length !== 6) {
                        return {
                            isValid: false,
                            message: 'Sort code must be 6 digits'
                        };
                    }
                    return {
                        isValid: true,
                        message: ''
                    };
                }
            },
            {
                id: 'profile_bankAccount',
                label: 'Bank Account',
                name: 'bankAccount',
                initialValue: data?.bankAccount,
                required: false,
                masked: true,
                validate: (value) => {
                    if (!value) {
                        return {
                            isValid: true,
                            message: ''
                        };
                    }
                    if (value.length > 15) {
                        return {
                            isValid: false,
                            message: 'Bank account cannot exceed 15 characters'
                        };
                    }
                    if (!/^[a-zA-Z0-9]+$/.test(value)) {
                        return {
                            isValid: false,
                            message: 'Bank account must contain only letters and numbers'
                        };
                    }
                    return {
                        isValid: true,
                        message: ''
                    };
                }
            }
        ], [data]);

    const {
        fields: basicInfoFields,
        getFieldValues: getBasicInfoFieldValues
    } = useTextFields(basicInfoTextFields);

    // Maria, 18 October 2026: date of birth, email and mobile number under the home address, as on the student profile
    const detailsUnderAddress = canViewBasicInformation && canViewHomeAddress;
    const DETAILS_UNDER_ADDRESS = ['dateOfBirth', 'email', 'mobileNumber'];
    const isDetailUnderAddress = name => detailsUnderAddress && DETAILS_UNDER_ADDRESS.includes(name);
    const pickFields = keep => Object.fromEntries(Object.entries(basicInfoFields).filter(([name]) => keep(name)));
    const nameFields = pickFields(name => !isDetailUnderAddress(name));
    const addressDetailFields = pickFields(isDetailUnderAddress);

    const {
        fields: identificationBankingFields,
        getFieldValues: getIdentificationBankingFieldValues
    } = useTextFields(identificationBankingDetailsTextFields);

    const {
        fields: nextOfKinFields,
        getFieldValues: getNextOfKinFieldsFieldValues
    } = useTextFields(nextOfKinTextFields);

    // Maria, 18 October 2026: Next of Kin in the order the names are read: title, names, relationship, telephone
    const NEXT_OF_KIN_ORDER = ['nextOfKinTitleCode', 'nextOfKinForename', 'nextOfKinMiddleName', 'nextOfKinSurname', 'nextOfKinTypeCode', 'nextOfKinTelephone'];
    const orderedNextOfKinFields = Object.fromEntries(NEXT_OF_KIN_ORDER.filter(name => nextOfKinFields[name]).map(name => [name, nextOfKinFields[name]]));

    const carFormFields = [
        {
            label: 'Registration',
            name: 'registration',
            required: true,
            validate: (value) => {
                if (!value || value.trim() === '') {
                    return {
                        isValid: false,
                        message: 'Registration is required'
                    };
                }
                if (value.length > 12) {
                    return {
                        isValid: false,
                        message: 'Registration cannot exceed 12 characters'
                    };
                }
                return {
                    isValid: true,
                    message: ''
                };
            }
        },
        {
            label: 'Color',
            name: 'color',
            required: false,
            validate: (value) => validateFieldLength(value, 32)
        },
        {
            label: 'Model',
            name: 'model',
            required: false,
            validate: (value) => validateFieldLength(value, 32)
        }
    ];

    const handleAddCar = () => {
        setEditingCar(null);
        setOpenCarDialog(true);
    };

    const handleEditCar = (car) => {
        setEditingCar(car);
        setOpenCarDialog(true);
    };

    const handleDeleteCar = (car) => {
        const remove = () => setCars(prev => prev.filter(c => c[LOCAL_ROW_ID_FIELD] !== car[LOCAL_ROW_ID_FIELD]));

        if (!rowsEditable) {
            remove();
            return;
        }
        confirm({
            title: 'Delete Car',
            description: `Are you sure you want to delete the car ${car.registration ?? ''}?`,
            confirmationText: 'Delete'
        })
            .then(() => {
                remove();
                setSaveRequest(n => n + 1);
            })
            .catch(() => {
            });
    };

    const handleSaveCar = (carData) => {
        const normalizedCarData = trimStringValues(carData);

        if (editingCar) {
            const updatedCar = {
                ...editingCar,
                ...normalizedCarData
            };

            setCars(prev => prev.map(c => (
                c[LOCAL_ROW_ID_FIELD] === editingCar[LOCAL_ROW_ID_FIELD]
                    ? updatedCar
                    : c
            )));
        } else {
            setCars(prev => [...prev, {
                ...normalizedCarData,
                [LOCAL_ROW_ID_FIELD]: `car-new-${carRowKeyRef.current++}`
            }]);
        }
        setEditingCar(null);
        setOpenCarDialog(false);
        if (rowsEditable) {
            setSaveRequest(n => n + 1);
        }
    };

    const handleAddContact = () => {
        setEditingContact(null);
        setOpenContactDialog(true);
    };

    const handleEditContact = (contact) => {
        setEditingContact(contact);
        setOpenContactDialog(true);
    };

    const handleDeleteContact = (contact) => {
        const remove = () => setContacts(prev => prev.filter(c => c[LOCAL_ROW_ID_FIELD] !== contact[LOCAL_ROW_ID_FIELD]));

        if (!rowsEditable) {
            remove();
            return;
        }
        confirm({
            title: 'Delete Contact',
            description: `Are you sure you want to delete ${[contact.forename, contact.surname].filter(Boolean).join(' ')}?`,
            confirmationText: 'Delete'
        })
            .then(() => {
                remove();
                setSaveRequest(n => n + 1);
            })
            .catch(() => {
            });
    };

    const handleSaveContact = (contactData) => {
        const normalizedContactData = trimStringValues(contactData);

        if (editingContact) {
            const updatedContact = {
                ...editingContact,
                ...normalizedContactData
            };

            setContacts(prev => prev.map(c => (
                c[LOCAL_ROW_ID_FIELD] === editingContact[LOCAL_ROW_ID_FIELD]
                    ? updatedContact
                    : c
            )));
        } else {
            setContacts(prev => [...prev, {
                ...normalizedContactData,
                [LOCAL_ROW_ID_FIELD]: `contact-new-${contactRowKeyRef.current++}`
            }]);
        }
        setEditingContact(null);
        setOpenContactDialog(false);
        if (rowsEditable) {
            setSaveRequest(n => n + 1);
        }
    };

    const toSaveAddress = (addr) => {
        if (!addr) return null;
        return {
            ...addr,
            country: addr?.countryCode
        };
    };

    const saveBody = () => ({
        id: data.id,
        ...getBasicInfoFieldValues(),
        ...getIdentificationBankingFieldValues(),
        ...getNextOfKinFieldsFieldValues(),
        address: toSaveAddress(selectedAddress),
        nextOfKinAddress: toSaveAddress(selectedNextOfKinAddress),
        cars: removeLocalRowIds(cars),
        contacts: removeLocalRowIds(contacts)
    });

    // A row's change is saved after the change has been drawn, so the save sends the new rows.
    useEffect(() => {
        if (saveRequest) {
            updateEmployeeProfile(saveBody());
        }
    }, [saveRequest]);

    const handleSave = () => {
        confirm({
            title: 'Confirmation',
            description: 'Are you sure that you want to update the profile?',
            confirmationText: 'Confirm'
        })
            .then(() => updateEmployeeProfile(saveBody()))
            .catch(() => {
            });
    };

    const isAddressValid = (address) => {
        if (!address) return true;
        const fields = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode'];
        const filled = fields.filter(f => address?.[f]);
        if (filled.length === 0) return true;
        return Boolean(address?.postcode || address?.town || address?.street);
    };

    const isNextOfKinValid = () => {
        const hasNextOfKinFields = Object.values(nextOfKinFields).some(field => {
            const value = field.textFieldProps.value;
            return value !== undefined && value !== null && value !== '';
        });

        const hasNextOfKinAddress = selectedNextOfKinAddress && Object.values(selectedNextOfKinAddress).some(value =>
            value !== undefined && value !== null && value !== ''
        );

        if (!hasNextOfKinFields && !hasNextOfKinAddress) return true;

        return nextOfKinFields.nextOfKinSurname?.textFieldProps.value !== undefined &&
            nextOfKinFields.nextOfKinSurname?.textFieldProps.value !== null &&
            nextOfKinFields.nextOfKinSurname?.textFieldProps.value !== '';
    };

    /* eslint-disable react/prop-types */
    const areTextFieldsValid = (fieldsObj, fieldDefinitions = []) => {
        if (!fieldsObj || typeof fieldsObj !== 'object') return false;
        const fieldDefinitionsMap = fieldDefinitions.reduce((acc, field) => {
            acc[field.name] = field;
            return acc;
        }, {});

        return Object.values(fieldsObj).every(f => {
            const props = f.textFieldProps;
            if (props.error) {
                return false;
            }

            if (!props.required || hasFieldValue(props.value)) {
                return true;
            }

            const initialValue = fieldDefinitionsMap[props.name]?.initialValue;

            return !props.changed && !hasFieldValue(initialValue);
        });
    };
    /* eslint-enable react/prop-types */

    const isFormValid =
        (!canEditBasicInformation || areTextFieldsValid(basicInfoFields, basicInfoTextFields)) &&
        (!canEditIdentificationAndBanking ||
            areTextFieldsValid(identificationBankingFields, identificationBankingDetailsTextFields)) &&
        (!canEditHomeAddress || isAddressValid(selectedAddress)) &&
        (!canEditNextOfKinAddress || isAddressValid(selectedNextOfKinAddress)) &&
        (!canEditNextOfKin || isNextOfKinValid());

    // In edit mode the Next of Kin table shows what is being edited, so a change shows before it is saved.
    const nextOfKinRow = isEditMode
        ? {
            ...Object.fromEntries(Object.entries(nextOfKinFields).map(([name, field]) => [name, field.textFieldProps.value])),
            nextOfKinAddress: selectedNextOfKinAddress
        }
        : data;

    const openNextOfKin = () => {
        setIsEditMode(true);
        setOpenNextOfKinDialog(true);
    };

    const handleClearNextOfKin = () => {
        confirm({
            title: 'Clear Next of Kin',
            description: 'Are you sure you want to clear all next of kin information?',
            confirmationText: 'Clear'
        })
            .then(() => {
                Object.keys(nextOfKinFields).forEach(fieldName => {
                    const field = nextOfKinFields[fieldName];
                    if (field?.textFieldProps?.onChange) {
                        field.textFieldProps.onChange({target: {name: fieldName, value: ''}});
                    }
                });

                setSelectedNextOfKinAddress(null);
                setNextOfKinAddressKey(prev => prev + 1);
                if (rowsEditable) {
                    setSaveRequest(n => n + 1);
                }
            })
            .catch(() => {
            });
    };

    const bankTabOpen = Boolean(sections?.includes('identificationAndBanking'));
    // Maria, 19 October 2026: on the Profile tab, a smaller Edit in the white space at the bottom of the first box
    const profileTabOpen = Boolean(sections?.includes('basicInformation')) && sideBySideMd === 6;
    // Maria, 19 October 2026: the question and answer columns fitted in each box on the Profile tab
    const fitNameColumns = useFitColumns();
    const fitAddressColumns = useFitColumns();
    const pageButtons = (isEditMode
                                ? (
                                    <Box display="flex" gap={1.5}>
                                        <Button
                                            size="small" sx={{width: 110}}
                                            variant="contained"
                                            onClick={handleSave}
                                            disabled={!isFormValid}
                                        >
                                            Save
                                        </Button>
                                        {/* Maria, 19 October 2026: a way out of edit mode without saving */}
                                        <Button size="small" sx={{width: 110}} variant="outlined" onClick={onCancel}>
                                            Cancel
                                        </Button>
                                    </Box>
                                )
                                : (
                                    isReadOnlyProfile || !canEditAnyStaffSection
                                        ? null
                                        : (
                                            <Button
                                                variant="contained"
                                                size="small" sx={{width: 110}}
                                                onClick={() => {
                                                    setSelectedAddress(data?.address || null);
                                                    setSelectedNextOfKinAddress(data?.nextOfKinAddress || null);
                                                    setCars(addLocalRowIds(data?.cars, 'car', carRowKeyRef));
                                                    setContacts(addLocalRowIds(data?.contacts, 'contact', contactRowKeyRef));
                                                    setIsEditMode(true);
                                                }}
                                            >
                                                {/* Maria, 19 October 2026: 'Edit Profile' on the Profile tab */}
                                                {profileTabOpen ? 'Edit Profile' : 'Edit'}
                                            </Button>
                                        )
                                )

    );

    return (
        <Box flexGrow={1} position="relative">
            {
                isSomeQueryPending
                    ? null
                    : <>
                        {data && isReadOnlyProfile && (
                            <Alert icon={<InfoIcon />} severity="info" sx={{mb: 2}}>
                                This profile is not linked to a SIMS staff record and cannot be edited. To link it, please ensure the email address matches the SIMS staff record.
                            </Alert>
                        )}
                        {data && (
                            <Grid container spacing={{
                                xs: 2,
                                md: 2
                            }}>
                                {hasLeftColumnSections && (
                                <Grid item xs={12} md={leftColumnMd}>
                                    {/* Maria, 19 October 2026: the Profile boxes end in line with the Cars tab */}
                                    <Grid container columnSpacing={2} ref={sideBySideMd === 6 ? contactsRef : undefined} sx={sideBySideMd === 6 && toCarsTab ? {width: toCarsTab} : undefined}>
                                    {canViewBasicInformation && (
                                        <Grid item xs={12} md={sideBySideMd} sx={{...EQUAL_HEIGHT_SX, position: 'relative', ...(firstBoxWidth ? {flexBasis: {md: `${firstBoxWidth}px`}, maxWidth: {md: `${firstBoxWidth}px`}} : {})}}>
                                        {/* Maria, 18 October 2026: no 'Basic Information' heading */}
                                        <ProfileInfoSection>
                                            {/* AD-2731 (Maria, 19 October 2026): the member of staff's photo from SIMS beside their
                                                details, as on the first page of the student profile */}
                                            <Stack direction="row" gap={2} alignItems="stretch">
                                                {profileTabOpen && (
                                                    // AD-2725 (Maria, 19 October 2026): the role above the photo, square and the same width, as an
                                                    // applicant's status is; the badge and photo together end level with the last line beside them
                                                    <Stack sx={{width: {xs: 96, md: 124}, flexShrink: 0, alignSelf: 'flex-start', containerType: 'inline-size'}}>
                                                        {data?.accountRole && (
                                                            <Box
                                                                data-testid="staff-profile-role"
                                                                title={(STAFF_ROLES[data.accountRole] ?? STAFF_ROLES.Employee).title}
                                                                sx={{
                                                                    backgroundColor: (STAFF_ROLES[data.accountRole] ?? STAFF_ROLES.Employee).background,
                                                                    color: (STAFF_ROLES[data.accountRole] ?? STAFF_ROLES.Employee).text,
                                                                    fontWeight: 600,
                                                                    // Maria, 19 October 2026: bigger writing and a deeper badge
                                                                    // Maria, 19 October 2026: the words get smaller to fit the badge on a phone, rather
                                                                    // than being cut off
                                                                    fontSize: 'min(15px, 12.5cqi)',
                                                                    lineHeight: 1.3,
                                                                    textAlign: 'center',
                                                                    px: 0.5,
                                                                    py: 0.75,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}
                                                            >
                                                                {(STAFF_ROLES[data.accountRole] ?? STAFF_ROLES.Employee).label}
                                                            </Box>
                                                        )}
                                                        <Avatar
                                                            variant="square"
                                                            // Maria, 19 October 2026: a fine line round the photo in the colour of the role badge
                                                            // Maria, 19 October 2026: the 3 by 4 shape of a school photo, the same in edit mode
                                                            sx={{width: '100%', height: 'auto', aspectRatio: '3 / 4', ...photoLineSx((STAFF_ROLES[data?.accountRole] ?? STAFF_ROLES.Employee).background)}}
                                                            alt={`${data?.preferredForename ?? ''} ${data?.preferredSurname ?? ''}`.trim()}
                                                            src={data?.photo ? `data:image/jpeg;base64,${data.photo}` : undefined}
                                                            data-testid="staff-profile-photo"
                                                        />
                                                    </Stack>
                                                )}
                                                <Box ref={fitNameColumns} sx={{flex: 1, minWidth: 0}}>
                                                    <EditableFields
                                                        fields={nameFields}
                                                        edit={isEditMode && canEditBasicInformation}
                                                        fitColumns={profileTabOpen}
                                                    />
                                                </Box>
                                            </Stack>
                                            {isEditMode && !canEditBasicInformation && <LockedSectionHint/>}
                                            {profileTabOpen && (
                                                // Maria, 19 October 2026: the buttons the same width as the photo, centred underneath it; in
                                                // edit mode Save and Cancel one under the other
                                                <Box sx={{
                                                    mt: 1,
                                                    width: {xs: 96, md: 124},
                                                    '& > .MuiBox-root': {flexDirection: 'column', gap: 1},
                                                    '& .MuiButton-root': {width: '100%', minWidth: 0, px: 0.5, py: 0.25, fontSize: 12}
                                                }}>
                                                    {pageButtons}
                                                </Box>
                                            )}
                                        </ProfileInfoSection>
                                        </Grid>
                                    )}

                                    {/* Maria, 18 October 2026: on the Banking tab, without a heading, as the line above it already
                                        says 'Bank and NI Details'. Maria, 19 October 2026: the full width of the page */}
                                    {canViewIdentificationAndBanking && (
                                        <Grid item xs={12} md={12}>
                                        <Box ref={sections ? contactsRef : undefined} sx={sections && toCarsTab ? {ml: `${indent}px`, width: toCarsTab, maxWidth: '100%'} : undefined}>
                                        <ProfileInfoSection title={sections ? undefined : 'Identification & Banking Details'}>
                                            {/* Maria, 19 October 2026: on the Bank and NI tab, Edit at the top right of the box and a narrower
                                                first column */}
                                            <Box sx={{position: 'relative'}}>
                                                <EditableFields
                                                    key={data?.id}
                                                    fields={identificationBankingFields}
                                                    edit={isEditMode && canEditIdentificationAndBanking}
                                                    labelColumn={bankTabOpen ? '120px minmax(0, 1fr)' : undefined}
                                                />
                                            </Box>
                                            {isEditMode && !canEditIdentificationAndBanking && <LockedSectionHint/>}
                                        </ProfileInfoSection>
                                        {/* Maria, 19 October 2026: Edit, Save and Cancel under the box, as on the student profile */}
                                        {bankTabOpen && (
                                            <Box sx={{mt: 1.5}} data-testid="staff-banking-buttons">
                                                {pageButtons}
                                            </Box>
                                        )}
                                        </Box>
                                        </Grid>
                                    )}

                                    {canViewHomeAddress && (
                                        <Grid item xs={12} md={sideBySideMd} sx={firstBoxWidth ? {...EQUAL_HEIGHT_SX, flexBasis: {md: 0}, flexGrow: {md: 1}, maxWidth: {md: 'none'}} : EQUAL_HEIGHT_SX}>
                                        <ProfileInfoSection title={sideBySideMd === 6 ? undefined : 'Home Address'}>
                                            <Box ref={fitAddressColumns}>
                                            {/* Maria, 18 October 2026: on the Profile tab, a small heading inside the box, as on the student profile */}
                                            {sideBySideMd === 6 && isEditMode && canEditHomeAddress && <Typography variant="body2" fontWeight="bold" sx={{mb: 0.5}}>Home Address</Typography>}
                                            {isEditMode && canEditHomeAddress
                                                ? <Box sx={COMPACT_ADDRESS_SX}><AdmicityAddressLookup
                                                    key={isEditMode && canEditHomeAddress ? 'home-edit' : `home-view-${homeAddressDataKey}`}
                                                    onAddressSelect={isEditMode && canEditHomeAddress ? setSelectedAddress : ignoreAddressSelect}
                                                    readonly={!isEditMode || !canEditHomeAddress}
                                                    initialValue={isEditMode ? selectedAddress : data?.address}
                                                    defaultCountry={''}
                                                /></Box>
                                                : sideBySideMd === 6 ? <AddressRow address={data?.address} fitColumns={profileTabOpen}/> : <AddressLines address={data?.address}/>}
                                            {isEditMode && !canEditHomeAddress && <LockedSectionHint/>}
                                            {Object.keys(addressDetailFields).length > 0 && (
                                                <Box sx={{mt: isEditMode ? 1 : 0}}>
                                                    <EditableFields
                                                        fields={addressDetailFields}
                                                        edit={isEditMode && canEditBasicInformation}
                                                        dateFormat="Do MMMM YYYY"
                                                        fitColumns={profileTabOpen}
                                                    />
                                                </Box>
                                            )}
                                            </Box>
                                        </ProfileInfoSection>
                                        </Grid>
                                    )}
                                    </Grid>
                                </Grid>
                                )}

                                {hasRightColumnSections && (
                                <Grid item xs={12} md={rightColumnMd}>
                                    {/* Maria, 18 October 2026: the cars as a table like the contacts table, with 'Add Car' under
                                        it */}
                                    {canViewCars && (
                                        <Box ref={contactsRef} sx={{mb: 2, ...(toCarsTab ? {width: toCarsTab, maxWidth: '100%', ml: `${indent}px`} : {})}}>
                                            <StaffContactTable
                                                rows={cars ?? []}
                                                rowKey={row => row[LOCAL_ROW_ID_FIELD]}
                                                emptyText="No cars have been added"
                                                columns={[
                                                    {id: 'registration', header: 'Registration', width: '86px', nowrap: true, cell: row => row.registration},
                                                    {id: 'color', header: 'Colour', width: '52px', nowrap: true, cell: row => row.color},
                                                    {id: 'model', header: 'Model', cell: row => row.model}
                                                ]}
                                                headerAction={(
                                                    <Button
                                                        color="primary"
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<AddCircleOutlineIcon/>}
                                                        onClick={handleAddCar}
                                                        sx={{fontSize: 13, py: 0.25, whiteSpace: 'nowrap'}}
                                                    >
                                                        Add Car
                                                    </Button>
                                                )}
                                                actions={(isEditMode || rowsEditable) && canEditCars
                                                    ? row => {
                                                        const notYetInSims = !row.simsId && !!row.id;
                                                        return (
                                                            <Tooltip title={notYetInSims ? 'The car cannot be edited before it will be synchronized with SIMS' : ''}>
                                                                <Box display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
                                                                    <Button variant="outlined" size="small" disabled={notYetInSims} onClick={() => handleEditCar(row)} sx={{minWidth: 0, px: 1.25, py: 0.25, fontSize: 13}}>Edit</Button>
                                                                    <Button variant="outlined" size="small" color="error" disabled={notYetInSims} onClick={() => handleDeleteCar(row)} sx={{minWidth: 0, px: 1.25, py: 0.25, fontSize: 13}}>Delete</Button>
                                                                </Box>
                                                            </Tooltip>
                                                        );
                                                    }
                                                    : undefined}
                                            />
                                            {isEditMode && !canEditCars && <LockedSectionHint/>}

                                            <AdmicityDialog
                                                handleClose={() => setOpenCarDialog(false)}
                                                title={editingCar ? 'Edit Car' : 'Add Car'}
                                                open={openCarDialog}
                                                actions={[]}
                                            >
                                                <AdmicityForm
                                                    key={editingCar?.[LOCAL_ROW_ID_FIELD] || 'new-car'}
                                                    id="car-form"
                                                    textFields={carFormFields.map(field => ({
                                                        ...field,
                                                        initialValue: editingCar ? editingCar[field.name] : ''
                                                    }))}
                                                    handleSubmit={handleSaveCar}
                                                    buttonText={editingCar ? 'Save' : 'Add'}
                                                    actionsContainerProps={{container: {display: 'flex'}}}
                                                />
                                            </AdmicityDialog>
                                        </Box>
                                    )}

                                    {/* Maria, 19 October 2026: Next of Kin above Other Contacts, both across the full width of the page,
                                        as the student contacts table is */}
                                    <Box ref={contactsRef} sx={{width: '100%'}}>
                                        <Box>
                                    {/* Maria, 18 October 2026: Next of Kin first, as a table in columns like the student contacts,
                                        with Other Contacts underneath */}
                                    {(canViewNextOfKin || canViewNextOfKinAddress) && (
                                        <Box sx={{mb: 2, position: 'relative'}}>
                                            <StaffContactTable
                                                dense
                                                rows={nextOfKinRow?.nextOfKinSurname ? [nextOfKinRow] : []}
                                                rowKey={() => 'next-of-kin'}
                                                emptyText="No next of kin has been added"
                                                columns={[
                                                    {
                                                        id: 'nokName',
                                                        // Maria, 19 October 2026: the heading says 'Next of Kin', in place of a title above the table
                                                        header: 'Next of Kin',
                                                        cell: row => (
                                                            <Box>
                                                                <Typography sx={{fontWeight: 600, fontSize: 15, lineHeight: 1.25}}>
                                                                    {[titles?.find(t => t.code === row.nextOfKinTitleCode)?.description,
                                                                        row.nextOfKinForename, row.nextOfKinMiddleName, row.nextOfKinSurname].filter(Boolean).join(' ')}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.25}}>
                                                                    {nextOfKinTypes?.find(t => t.code === row.nextOfKinTypeCode)?.description ?? ''}
                                                                </Typography>
                                                            </Box>
                                                        )
                                                    },
                                                    ...(canViewNextOfKin ? [{id: 'nokPhone', header: 'Phone Number', cell: row => row.nextOfKinTelephone}] : []),
                                                    ...(canViewNextOfKinAddress ? [{id: 'nokAddress', header: 'Address', cell: row => <AddressLines address={row.nextOfKinAddress} plain tight/>}] : [])
                                                ]}
                                                actions={(isEditMode || rowsEditable) && (canEditNextOfKin || canEditNextOfKinAddress)
                                                    ? () => (
                                                        <Box display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
                                                            <Button variant="outlined" size="small" onClick={openNextOfKin} sx={{minWidth: 0, px: 1.25, py: 0.25, fontSize: 13}}>Edit</Button>
                                                            {canEditNextOfKin && <Button variant="outlined" size="small" color="error" onClick={handleClearNextOfKin} sx={{minWidth: 0, px: 1.25, py: 0.25, fontSize: 13}}>Delete</Button>}
                                                        </Box>
                                                    )
                                                    : undefined}
                                            />
                                            {(isEditMode || rowsEditable) && canEditNextOfKin && !nextOfKinRow?.nextOfKinSurname && (
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    startIcon={<AddCircleOutlineIcon/>}
                                                    onClick={openNextOfKin}
                                                    size="small"
                                                    sx={{mt: 1.5}}
                                                >
                                                    Add Next of Kin
                                                </Button>
                                            )}
                                            {isEditMode && !canEditNextOfKin && !canEditNextOfKinAddress && <LockedSectionHint/>}
                                        </Box>
                                    )}
                                    {/* Maria, 19 October 2026: the Next of Kin box in the same style as Add Contact, on two screens:
                                        the person's details, then the address */}
                                    <Dialog
                                        open={openNextOfKinDialog && isEditMode}
                                        onClose={rowsEditable && onCancel ? onCancel : () => setOpenNextOfKinDialog(false)}
                                        fullWidth
                                        maxWidth="sm"
                                        data-testid="next-of-kin-dialog"
                                        PaperProps={{sx: {borderRadius: 3, height: 'min(600px, calc(100% - 64px))'}}}
                                    >
                                        <Box sx={{px: 4, pt: 2, pb: 1.5, borderBottom: '1px solid #eef0f3'}}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="overline" sx={{color: '#0DAD95', fontWeight: 700, letterSpacing: 1}}>
                                                    {nextOfKinRow?.nextOfKinSurname ? 'Edit Next of Kin' : 'Add Next of Kin'}
                                                </Typography>
                                                <IconButton size="small" aria-label="Close" onClick={rowsEditable && onCancel ? onCancel : () => setOpenNextOfKinDialog(false)}>
                                                    <CloseIcon fontSize="small"/>
                                                </IconButton>
                                            </Stack>
                                            <Typography variant="h5" sx={{fontWeight: 600}} data-testid="next-of-kin-question">
                                                {nextOfKinStep === 'details' ? 'Next of Kin Details' : 'Next of Kin Address'}
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
                                                {['details', 'address'].map((name, index) => (
                                                    <Box key={name} sx={{flex: 1, height: 4, borderRadius: 2, backgroundColor: index <= (nextOfKinStep === 'details' ? 0 : 1) ? '#186BBE' : '#e3e8ef'}}/>
                                                ))}
                                            </Stack>
                                        </Box>
                                        <DialogContent sx={{px: 4, pt: 3, pb: 3}}>
                                            {nextOfKinStep === 'details' && (
                                                <Box sx={{maxWidth: 480, mx: 'auto'}}>
                                                    {canViewNextOfKin && (
                                                        <EditableFields
                                                            fields={orderedNextOfKinFields}
                                                            edit={isEditMode && canEditNextOfKin}
                                                        />
                                                    )}
                                                    {isEditMode && !canEditNextOfKin && <LockedSectionHint/>}
                                                </Box>
                                            )}
                                            {nextOfKinStep === 'address' && (
                                                <Box sx={{maxWidth: 480, mx: 'auto'}}>
                                                    {canViewNextOfKinAddress && (isEditMode && canEditNextOfKinAddress
                                                        ? <Box sx={COMPACT_ADDRESS_SX}><AdmicityAddressLookup
                                                key={
                                                    isEditMode && canEditNextOfKinAddress
                                                        ? `nok-edit-${nextOfKinAddressKey}`
                                                        : `nok-view-${nextOfKinAddressDataKey}`
                                                }
                                                onAddressSelect={
                                                    isEditMode && canEditNextOfKinAddress
                                                        ? setSelectedNextOfKinAddress
                                                        : ignoreAddressSelect
                                                }
                                                readonly={
                                                    !isEditMode ||
                                                    !canEditNextOfKinAddress ||
                                                    !nextOfKinFields.nextOfKinSurname?.textFieldProps.value
                                                }
                                                defaultCountry={''}
                                                initialValue={isEditMode ? selectedNextOfKinAddress : data?.nextOfKinAddress}
                                            /></Box>
                                                        : <AddressRow address={data?.nextOfKinAddress} label="Address"/>)}
                                                    {isEditMode && !canEditNextOfKinAddress && <LockedSectionHint/>}
                                                </Box>
                                            )}
                                        </DialogContent>
                                        <Box sx={{px: 4, py: 2, borderTop: '1px solid #eef0f3'}}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Button
                                                    variant="outlined"
                                                    data-testid="next-of-kin-back"
                                                    onClick={nextOfKinStep === 'address' ? () => setNextOfKinStep('details') : (rowsEditable && onCancel ? onCancel : () => setOpenNextOfKinDialog(false))}
                                                >
                                                    Back
                                                </Button>
                                                {nextOfKinStep === 'details'
                                                    ? (
                                                        <Button
                                                            variant="contained"
                                                            data-testid="next-of-kin-next"
                                                            disabled={!isNextOfKinValid()}
                                                            onClick={() => setNextOfKinStep('address')}
                                                            sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                                                        >
                                                            Next
                                                        </Button>
                                                    )
                                                    : rowsEditable
                                                        ? (
                                                            <Button
                                                                variant="contained"
                                                                data-testid="next-of-kin-save"
                                                                disabled={!isNextOfKinValid() || !isAddressValid(selectedNextOfKinAddress)}
                                                                onClick={() => {
                                                                    setOpenNextOfKinDialog(false);
                                                                    setSaveRequest(n => n + 1);
                                                                }}
                                                                sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                                                            >
                                                                Save
                                                            </Button>
                                                        )
                                                        : <Button variant="contained" onClick={() => setOpenNextOfKinDialog(false)}>Done</Button>}
                                            </Stack>
                                        </Box>
                                    </Dialog>

                                        </Box>
                                        <Box>
                                    {canViewContacts && (
                                        <Typography variant="body2" fontWeight="bold" sx={SECTION_HEADING_SX}>Other Contacts</Typography>
                                    )}
                                    {/* Maria, 18 October 2026: the contacts as a table like the student contacts table, with the
                                        type under the name and 'Add Contact' under the table */}
                                    {canViewContacts && (
                                        <Box sx={{mb: 2, position: 'relative'}}>
                                            <StaffContactTable
                                                dense
                                                rows={contacts ?? []}
                                                rowKey={row => row[LOCAL_ROW_ID_FIELD]}
                                                emptyText="No contacts have been added"
                                                columns={[
                                                    {
                                                        id: 'name',
                                                        header: 'Name',
                                                        cell: row => (
                                                            <Box>
                                                                <Typography sx={{fontWeight: 600, fontSize: 15, lineHeight: 1.25}}>
                                                                    {[row.forename, row.surname].filter(Boolean).join(' ')}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.25}}>
                                                                    {employeeContactTypes.find(t => t.code === row.typeCode)?.description ?? ''}
                                                                </Typography>
                                                            </Box>
                                                        )
                                                    },
                                                    {id: 'phone', header: 'Phone Number', cell: row => row.phone},
                                                    {id: 'email', header: 'Email', cell: row => row.email}
                                                ]}
                                                actions={(isEditMode || rowsEditable) && canEditContacts
                                                    ? row => {
                                                        const notYetInSims = !row.simsId && !!row.id;
                                                        return (
                                                            <Tooltip title={notYetInSims ? 'The contact cannot be edited before it will be synchronized with SIMS' : ''}>
                                                                <Box display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
                                                                    <Button variant="outlined" size="small" disabled={notYetInSims} onClick={() => handleEditContact(row)} sx={{minWidth: 0, px: 1.25, py: 0.25, fontSize: 13}}>Edit</Button>
                                                                    <Button variant="outlined" size="small" color="error" disabled={notYetInSims} onClick={() => handleDeleteContact(row)} sx={{minWidth: 0, px: 1.25, py: 0.25, fontSize: 13}}>Delete</Button>
                                                                </Box>
                                                            </Tooltip>
                                                        );
                                                    }
                                                    : undefined}
                                            />
                                            {canEditContacts && (
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<AddCircleOutlineIcon/>}
                                                    onClick={handleAddContact}
                                                    sx={{mt: 1.5}}
                                                    data-testid="staff-add-contact"
                                                >
                                                    Add Contact
                                                </Button>
                                            )}
                                            {isEditMode && !canEditContacts && <LockedSectionHint/>}

                                            {/* Maria, 19 October 2026: the staff Add Contact box, one question at a time, like the student one */}
                                            <StaffAddContactBox
                                                open={openContactDialog}
                                                mode={editingContact ? 'edit' : 'add'}
                                                existingContact={editingContact}
                                                contactTypes={employeeContactTypes ?? []}
                                                onSave={handleSaveContact}
                                                onClose={() => setOpenContactDialog(false)}
                                            />
                                        </Box>
                                    )}
                                        </Box>
                                    </Box>

                                </Grid>
                                )}
                            </Grid>
                        )}

                        {/* Maria, 18 October 2026: 'Edit' and 'Save' on the left under the boxes, as on the student profile */}
                        <Box display={rowsEditable || bankTabOpen || profileTabOpen ? 'none' : 'flex'} justifyContent={'flex-start'} sx={{mt: -0.5, ml: `${indent}px`}}>
                            {pageButtons}
                        </Box>
                    </>
            }

            <Backdrop
                sx={{
                    position: 'absolute',
                    backgroundColor: 'rgb(250, 250, 251)',
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
                open={isSomeQueryPending}
            >
                <Stack flexDirection="column" alignItems="center">
                    <CircularProgress size={40}/>
                </Stack>
            </Backdrop>
        </Box>
    );
};

EmployeeProfile.propTypes = {
    sections: PropTypes.arrayOf(PropTypes.string),
    data: PropTypes.object,
    isLoading: PropTypes.bool,
    staffPermissions: PropTypes.arrayOf(PropTypes.string),
    onCancel: PropTypes.func,
    framed: PropTypes.bool,
};

export default EmployeeProfile;
